/**
 * SoleadLab 2.0 - EXIF Utilities
 *
 * Extracts GPS coordinates and metadata from images using exifr library.
 */

import exifr from 'exifr';

export interface GeoPhotoData {
  coordinates: [number, number] | null;
  bearing: number | null;
  altitude: number | null;
  relativeAltitude: number | null;
  accuracy: number | null;
  captureDate: string | null;
  /** Full capture timestamp (ISO, second-resolution) when available; the
   *  observation time used for an imported indication's createdAt. */
  capturedAt: string | null;
  // DJI gimbal & flight attitude (from XMP)
  gimbalPitch: number | null;    // GimbalPitchDegree (e.g., -62.8 = 27.2° from nadir)
  gimbalYaw: number | null;      // GimbalYawDegree (e.g., -1.1 = nearly north)
  gimbalRoll: number | null;     // GimbalRollDegree
  flightYaw: number | null;      // FlightYawDegree (drone body heading)
  exifData: Record<string, unknown>;
}

/**
 * Extract GPS coordinates and metadata from an image file
 */
export async function extractExifData(file: File): Promise<GeoPhotoData> {
  try {
    // Parse EXIF + GPS data
    const exif = await exifr.parse(file, {
      gps: true,
      pick: ['GPSLatitude', 'GPSLongitude', 'GPSImgDirection', 'GPSAltitude', 'GPSAltitudeRef', 'GPSHPositioningError', 'DateTimeOriginal', 'CreateDate'],
    });

    if (!exif) {
      return {
        coordinates: null,
        bearing: null,
        altitude: null,
        relativeAltitude: null,
        accuracy: null,
        captureDate: null,
        capturedAt: null,
        gimbalPitch: null,
        gimbalYaw: null,
        gimbalRoll: null,
        flightYaw: null,
        exifData: {},
      };
    }

    // Extract GPS coordinates
    let coordinates: [number, number] | null = null;
    if (exif.latitude !== undefined && exif.longitude !== undefined) {
      coordinates = [exif.longitude, exif.latitude];
    }

    // Extract bearing (GPSImgDirection)
    let bearing: number | null = null;
    if (exif.GPSImgDirection !== undefined) {
      bearing = exif.GPSImgDirection;
    }

    // Extract altitude (raw EXIF tag name — gps:true only auto-translates lat/lon)
    let altitude: number | null = null;
    const rawAlt = exif.GPSAltitude ?? exif.altitude;
    if (rawAlt !== undefined && isFinite(rawAlt)) {
      // GPSAltitudeRef: 0 = above sea level, 1 = below sea level
      const altRef = exif.GPSAltitudeRef;
      altitude = (altRef === 1) ? -rawAlt : rawAlt;
    }

    // Extract accuracy (raw EXIF tag name)
    let accuracy: number | null = null;
    const rawAcc = exif.GPSHPositioningError ?? exif.horizontalAccuracy;
    if (rawAcc !== undefined && isFinite(rawAcc)) {
      accuracy = rawAcc;
    }

    // Extract capture date (day) and the full capture timestamp (with time).
    let captureDate: string | null = null;
    let capturedAt: string | null = null;
    const dateField = exif.DateTimeOriginal || exif.CreateDate;
    if (dateField instanceof Date) {
      captureDate = dateField.toISOString().split('T')[0];
      capturedAt = dateField.toISOString();
    } else if (typeof dateField === 'string') {
      // Parse EXIF date format: "YYYY:MM:DD HH:MM:SS"
      const m = dateField.match(/^(\d{4}):(\d{2}):(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/);
      if (m) {
        captureDate = `${m[1]}-${m[2]}-${m[3]}`;
        capturedAt = m[4] ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}` : captureDate;
      }
    }

    // Try to extract DJI XMP metadata (gimbal angles, relative altitude, flight attitude)
    let relativeAltitude: number | null = null;
    let gimbalPitch: number | null = null;
    let gimbalYaw: number | null = null;
    let gimbalRoll: number | null = null;
    let flightYaw: number | null = null;
    try {
      const xmpData = await exifr.parse(file, { xmp: true, tiff: false, ifd0: false, exif: false, gps: false } as Record<string, unknown>);
      if (xmpData) {
        // exifr merges XMP namespaces — DJI fields appear without namespace prefix
        const parseXmpNum = (val: unknown): number | null => {
          if (val === undefined || val === null) return null;
          const n = typeof val === 'string' ? parseFloat(val) : Number(val);
          return isFinite(n) ? n : null;
        };

        relativeAltitude = parseXmpNum(xmpData.RelativeAltitude ?? xmpData['drone-dji:RelativeAltitude']);
        gimbalPitch = parseXmpNum(xmpData.GimbalPitchDegree ?? xmpData['drone-dji:GimbalPitchDegree']);
        gimbalYaw = parseXmpNum(xmpData.GimbalYawDegree ?? xmpData['drone-dji:GimbalYawDegree']);
        gimbalRoll = parseXmpNum(xmpData.GimbalRollDegree ?? xmpData['drone-dji:GimbalRollDegree']);
        flightYaw = parseXmpNum(xmpData.FlightYawDegree ?? xmpData['drone-dji:FlightYawDegree']);
      }
    } catch {
      // XMP parsing not available or failed — silently continue
    }

    return {
      coordinates,
      bearing,
      altitude,
      relativeAltitude,
      accuracy,
      captureDate,
      capturedAt,
      gimbalPitch,
      gimbalYaw,
      gimbalRoll,
      flightYaw,
      exifData: exif,
    };
  } catch (error) {
    console.error('EXIF extraction error:', error);
    return {
      coordinates: null,
      bearing: null,
      altitude: null,
      relativeAltitude: null,
      accuracy: null,
      captureDate: null,
      capturedAt: null,
      gimbalPitch: null,
      gimbalYaw: null,
      gimbalRoll: null,
      flightYaw: null,
      exifData: {},
    };
  }
}

/**
 * Process multiple image files and extract geo data
 */
export async function processGeoPhotos(
  files: FileList | File[]
): Promise<{ file: File; data: GeoPhotoData }[]> {
  const results: { file: File; data: GeoPhotoData }[] = [];
  const imageExts = /\.(jpe?g|png|gif|webp|heic|heif|tiff?|dng|bmp)$/i;

  for (const file of Array.from(files)) {
    // Accept if MIME type says image OR if the file extension looks like an image
    const isImage = file.type.startsWith('image/') || imageExts.test(file.name);
    if (!isImage) continue;

    const data = await extractExifData(file);
    results.push({ file, data });
  }

  return results;
}
